import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';

const Attendance = () => {
    const { authFetch, user } = useContext(AppContext);
    const { t } = useTranslation();
    const isManager = user?.role === 'Admin' || user?.role === 'Manager' || user?.role === 'Controller';

    const [attendanceRows, setAttendanceRows] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [filterDate, setFilterDate] = useState(new Date().toISOString().slice(0, 10));
    
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [form, setForm] = useState({ employee_id: '', date: filterDate, clock_in: '', clock_out: '', status: 'Present' });

    const fetchEmployees = () => {
        authFetch('/api/employees').then(r => r.json()).then(d => setEmployees(Array.isArray(d) ? d : []));
    };

    const fetchAttendance = () => {
        authFetch(`/api/attendance?date=${filterDate}`)
            .then(r => r.json())
            .then(d => setAttendanceRows(Array.isArray(d) ? d : []));
    };

    useEffect(() => {
        fetchEmployees();
    }, []);

    useEffect(() => {
        fetchAttendance();
    }, [filterDate]);

    const handleSave = async (e) => {
        e.preventDefault();
        const res = await authFetch('/api/attendance', { method: 'POST', body: JSON.stringify(form) });
        if (res.ok) {
            setIsAddModalOpen(false);
            fetchAttendance();
            // keep form defaults but ready for next
            setForm({ ...form, employee_id: '', clock_in: '', clock_out: '', status: 'Present' });
        } else {
            alert(t('Error saving attendance'));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Delete this attendance record?'))) return;
        const res = await authFetch(`/api/attendance/${id}`, { method: 'DELETE' });
        if (res.ok) fetchAttendance();
    };

    const columns = [
        { 
            header: t('Employee'), accessor: 'employee_name',
            render: r => (
                <div>
                    <span className="font-bold text-gray-800">{r.employee_name}</span>
                    {r.department_name && <p className="text-xs text-gray-400">{r.department_name}</p>}
                </div>
            )
        },
        { 
            header: t('Date'), accessor: 'date',
            render: r => new Date(r.date).toLocaleDateString()
        },
        { 
            header: t('Clock In'), accessor: 'clock_in', 
            render: r => r.clock_in ? <span className="text-indigo-600 font-bold">{r.clock_in}</span> : '—'
        },
        { 
            header: t('Clock Out'), accessor: 'clock_out',
            render: r => r.clock_out ? <span className="text-orange-600 font-bold">{r.clock_out}</span> : '—'
        },
        { 
            header: t('Status'), accessor: 'status',
            render: r => {
                let badgeClass = 'bg-gray-100 text-gray-600';
                if (r.status === 'Present') badgeClass = 'bg-emerald-100 text-emerald-700';
                if (r.status === 'Absent') badgeClass = 'bg-red-100 text-red-700';
                if (r.status === 'Late') badgeClass = 'bg-yellow-100 text-yellow-700';
                if (r.status === 'Half-day') badgeClass = 'bg-blue-100 text-blue-700';
                return <span className={`px-2 py-1 rounded-full text-xs font-bold ${badgeClass}`}>{t(r.status)}</span>;
            }
        },
        {
            header: t('Actions'), accessor: 'actions',
            render: r => (
                <div className="flex gap-2">
                    {isManager && (
                        <button className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200" onClick={() => handleDelete(r.id)}>{t('Delete')}</button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">{t('Attendance')}</h2>
                    <p className="text-gray-500 text-sm mt-1">{t('Track daily employee presence and shifts')}</p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <input 
                        type="date" 
                        value={filterDate} 
                        onChange={e => setFilterDate(e.target.value)} 
                        className="border p-2 rounded shadow-sm focus:ring-2 focus:ring-indigo-500"
                    />
                    {isManager && (
                        <button onClick={() => setIsAddModalOpen(true)} className="bg-indigo-600 text-white px-4 py-2 rounded shadow hover:bg-indigo-700 transition font-bold">
                            + {t('Log Attendance')}
                        </button>
                    )}
                </div>
            </div>

            <DataTable columns={columns} data={attendanceRows} />

            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title={t('Log Attendance')}>
                <form onSubmit={handleSave} className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700">{t('Employee')}</label>
                        <select required className="w-full border p-2 rounded mt-1" value={form.employee_id} onChange={e => setForm({...form, employee_id: e.target.value})}>
                            <option value="">{t('Select Employee')}</option>
                            {employees.map(e => <option key={e.id} value={e.id}>{e.name} {e.department_name ? `(${e.department_name})` : ''}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700">{t('Date')}</label>
                        <input required type="date" className="w-full border p-2 rounded mt-1" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700">{t('Clock In')}</label>
                            <input type="time" className="w-full border p-2 rounded mt-1" value={form.clock_in} onChange={e => setForm({...form, clock_in: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700">{t('Clock Out')}</label>
                            <input type="time" className="w-full border p-2 rounded mt-1" value={form.clock_out} onChange={e => setForm({...form, clock_out: e.target.value})} />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700">{t('Status')}</label>
                        <select className="w-full border p-2 rounded mt-1" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                            <option value="Present">{t('Present')}</option>
                            <option value="Absent">{t('Absent')}</option>
                            <option value="Late">{t('Late')}</option>
                            <option value="Half-day">{t('Half-day')}</option>
                        </select>
                    </div>
                    <button type="submit" className="w-full bg-indigo-600 text-white py-2 rounded font-bold hover:bg-indigo-700">{t('Save Attendance')}</button>
                </form>
            </Modal>
        </div>
    );
};

export default Attendance;
